import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import {Box} from "@mui/material";

const AllReviews = () => {
  const location = useLocation();
  // Clicking a review notification (see notificationsPage.js) navigates
  // here with location.state.autoOpenReviewId — this opens that review's
  // details modal once the list has loaded. Consumed once so it doesn't
  // reopen if the user closes the modal and something else re-renders.
  const autoOpenConsumedRef = React.useRef(false);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [userImages, setUserImages] = useState({});
  const [productImages, setProductImages] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectingReviewId, setRejectingReviewId] = useState(null);
  const [approveLoading, setApproveLoading] = useState({});
  const [rejectLoading, setRejectLoading] = useState({});
  const [featuredReviews, setFeaturedReviews] = useState({}); // { [reviewId]: true/false }
  const [featuredLoading, setFeaturedLoading] = useState({}); // loading state per review

  useEffect(() => {
    const targetReviewId = location.state?.autoOpenReviewId;
    if (!targetReviewId || autoOpenConsumedRef.current) return;
    if (reviews.length === 0) return; // list hasn't loaded yet — try again once it does

    const match = reviews.find((r) => String(r.id) === String(targetReviewId));
    if (!match) return; // loaded but not found (e.g. deleted) — nothing to open

    autoOpenConsumedRef.current = true;
    handleViewReview(match);
  }, [location.state, reviews]);
  
  // Media states
  const [reviewMedia, setReviewMedia] = useState({});
  const [currentMediaIndex, setCurrentMediaIndex] = useState({});
  const [mediaLoading, setMediaLoading] = useState({});
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
    position: 'top-right'
  });

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState({
    show: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // API Configuration
 const API_BASE_URL = 'https://car.amar.co.jp/api';

// Get token — searches all common keys and any JWT in localStorage/sessionStorage
const getAuthToken = () => {
  const knownKeys = ["adminAuthToken", "authToken", "auth_token", "token", "accessToken", "jwt", "jwtToken", "adminToken", "userToken", "access_token"];

  for (const key of knownKeys) {
    const val = localStorage.getItem(key);
    if (val && val.startsWith("ey")) return val;
  }

  for (let i = 0; i < localStorage.length; i++) {
    const val = localStorage.getItem(localStorage.key(i));
    if (val && val.startsWith("ey") && val.includes(".")) return val;
  }

  for (const key of knownKeys) {
    const val = sessionStorage.getItem(key);
    if (val && val.startsWith("ey")) return val;
  }

  console.warn('No authentication token found');
  return null;
};

// Headers configuration with dynamic token
const getHeaders = (includeContentType = true) => {
  const token = getAuthToken();
  
  const headers = {
    'accept': 'application/json',
    'Accept-Language': 'en',
  };
  
  // Add token if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

// Show notification function
const showNotification = (message, type = 'success', duration = 3000) => {
  setNotification({
    show: true,
    message,
    type,
    position: 'top-right'
  });

  setTimeout(() => {
    setNotification(prev => ({ ...prev, show: false }));
  }, duration);
};

// Show confirmation dialog
const showConfirmationDialog = ({ 
  title, 
  message, 
  type = 'warning',
  onConfirm, 
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel'
}) => {
  setConfirmationDialog({
    show: true,
    title,
    message,
    type,
    onConfirm: () => {
      setConfirmationDialog(prev => ({ ...prev, show: false }));
      onConfirm();
    },
    onCancel: () => {
      setConfirmationDialog(prev => ({ ...prev, show: false }));
      onCancel && onCancel();
    },
    confirmText,
    cancelText
  });
};

// Fetch user image with dynamic token
const fetchUserImage = async (userId, profilePhoto) => {
  try {
    // Return cached image if available
    if (userImages[userId]) {
      return userImages[userId];
    }

    // If no profile photo, return placeholder
    if (!profilePhoto) {
      const placeholder = `https://ui-avatars.com/api/?name=User+${userId}&background=random&color=fff`;
      return placeholder;
    }

    // Try to fetch from the server with dynamic token
    const token = getAuthToken();
    if (token) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/authorization/user/get-image/`,
          {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          
          setUserImages(prev => ({
            ...prev,
            [userId]: imageUrl
          }));
          
          return imageUrl;
        }
      } catch (error) {
        console.warn(`Could not fetch user image for ${userId}:`, error);
      }
    }

    // Fallback: Check if profilePhoto is a URL
    if (profilePhoto.startsWith('http')) {
      return profilePhoto;
    }

    // Fallback: Use UI Avatars
    const placeholder = `https://ui-avatars.com/api/?name=User+${userId}&background=random&color=fff`;
    return placeholder;

  } catch (error) {
    console.error(`Error in fetchUserImage for user ${userId}:`, error);
    return `https://ui-avatars.com/api/?name=User+${userId}&background=random&color=fff`;
  }
};

// Fetch car image
const fetchCarImage = async (carModelId) => {
  try {
    if (productImages[carModelId]) {
      return productImages[carModelId];
    }

    const response = await fetch(
      `${API_BASE_URL}/image/car-exterior-images-1/get-image/${carModelId}`,
      {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Accept-Language': 'en'
        }
      }
    );

    if (response.ok) {
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      setProductImages(prev => ({
        ...prev,
        [carModelId]: imageUrl
      }));
      
      return imageUrl;
    } else {
      const fallbackImages = [
        'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1555212697-194d092e3b8f?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1563720223485-8d6d5c5c8c6b?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop'
      ];
      return fallbackImages[carModelId % fallbackImages.length];
    }
  } catch (error) {
    console.error(`Error fetching car image for model ${carModelId}:`, error);
    return 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop';
  }
};

// Fetch media for a specific review
const fetchReviewMedia = async (reviewId) => {
  if (reviewMedia[reviewId]) {
    return reviewMedia[reviewId];
  }

  setMediaLoading(prev => ({ ...prev, [reviewId]: true }));

  try {
    const response = await fetch(
      `${API_BASE_URL}/review-rating/review-rating/media/${reviewId}`,
      {
        method: 'GET',
        headers: getHeaders(false)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success && data.data.allMedia && data.data.allMedia.length > 0) {
      // Process media files
      const mediaFiles = data.data.allMedia.map(media => {
        if (media.mediaType === 'PHOTO' && media.base64Content) {
          return {
            type: 'photo',
            src: `data:image/png;base64,${media.base64Content}`,
            fileName: media.fileName,
            mediaId: media.mediaId
          };
        } else if (media.mediaType === 'VIDEO' && media.base64Content) {
          return {
            type: 'video',
            src: `data:video/mp4;base64,${media.base64Content}`,
            fileName: media.fileName,
            mediaId: media.mediaId
          };
        }
        return null;
      }).filter(Boolean);

      setReviewMedia(prev => ({
        ...prev,
        [reviewId]: mediaFiles
      }));

      // Initialize current media index for this review
      if (mediaFiles.length > 0) {
        setCurrentMediaIndex(prev => ({
          ...prev,
          [reviewId]: 0
        }));
      }

      return mediaFiles;
    }
    return [];
  } catch (error) {
    console.error(`Error fetching media for review ${reviewId}:`, error);
    return [];
  } finally {
    setMediaLoading(prev => ({ ...prev, [reviewId]: false }));
  }
};

// Navigate media carousel
const nextMedia = (reviewId) => {
  const media = reviewMedia[reviewId];
  if (!media || media.length === 0) return;

  setCurrentMediaIndex(prev => ({
    ...prev,
    [reviewId]: (prev[reviewId] + 1) % media.length
  }));
};

const prevMedia = (reviewId) => {
  const media = reviewMedia[reviewId];
  if (!media || media.length === 0) return;

  setCurrentMediaIndex(prev => ({
    ...prev,
    [reviewId]: prev[reviewId] === 0 ? media.length - 1 : prev[reviewId] - 1
  }));
};

// Fetch reviews from API
const fetchReviews = async (page = 0, size = 10) => {
  try {
    setLoading(true);
    const response = await fetch(
      `${API_BASE_URL}/review-rating/review-rating/admin/all-reviews?page=${page}&size=${size}`,
      {
        method: 'GET',
        headers: getHeaders(false)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      const processedReviews = await Promise.all(
        data.data.reviews.map(async (review) => {
          const userAvatar = await fetchUserImage(review.userId, review.profilePhoto);
          const productImage = await fetchCarImage(review.carModelId);
          
          return {
            id: review.ratingId,
            apiData: review,
            user: {
              name: `${review.firstName} ${review.lastName}`,
              email: review.userEmail || 'user@example.com',
              avatar: userAvatar,
              phone: 'N/A',
              location: review.userCountry || 'Unknown',
              joinDate: '2023-01-01'
            },
            rating: review.rating,
            comment: review.shareYourExperince,
            title: review.reviewTitle,
            product: {
              id: `CAR-${review.carModelId}`,
              name: `${review.carBrandName} ${review.carModelName}`,
              image: productImage,
              category: review.carCategoriesName,
              price: '$0',
              vin: 'N/A',
              condition: 'Used',
              color: 'Unknown',
              mileage: 'Unknown',
              brand: review.carBrandName,
              model: review.carModelName,
              carModelId: review.carModelId
            },
            date: `${review.createdAt[0]}-${String(review.createdAt[1]).padStart(2, '0')}-${String(review.createdAt[2]).padStart(2, '0')}`,
            status: review.reviewStatus.toLowerCase(),
            helpfulCount: Math.floor(Math.random() * 20),
            verifiedPurchase: review.verifiedPurchase,
            shippingOption: review.shippingOption,
            ratingBadge: review.ratingBadge,
            images: [],
            adminNotes: '',
            isAdminApproveRating: review.isAdminApproveRating,
            orderId: review.orderId,
            userId: review.userId,
            rejectionReason: review.rejectionReason,
            rejectedBy: review.rejectedBy,
            rejectedAt: review.rejectedAt,
            approvedBy: review.approvedBy,
            approvedAt: review.approvedAt
          };
        })
      );

      setReviews(processedReviews);
      setCurrentPage(data.data.currentPage);
      setTotalPages(data.data.totalPages);

    } else {
      console.error('API Error:', data.message);
      setReviews([]);
      showNotification(`Failed to load reviews: ${data.message}`, 'error');
    }
  } catch (error) {
    console.error('Error fetching reviews:', error);
    setReviews([]);
    showNotification('Error loading reviews. Please try again.', 'error');
  } finally {
    setLoading(false);
  }
};

// API: Approve a review with dynamic token
const approveReview = async (reviewId) => {
  setApproveLoading(prev => ({ ...prev, [reviewId]: true }));
  
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await fetch(
      `${API_BASE_URL}/review-rating/review-rating/approve/${reviewId}`,
      {
        method: 'POST',
        headers: getHeaders(false)
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error approving review:', error);
    throw error;
  } finally {
    setApproveLoading(prev => ({ ...prev, [reviewId]: false }));
  }
};

// API: Reject a review with dynamic token
const rejectReview = async (reviewId, reason) => {
  setRejectLoading(prev => ({ ...prev, [reviewId]: true }));
  
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await fetch(
      `${API_BASE_URL}/review-rating/review-rating/reject/${reviewId}`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          rejectionReason: reason || 'Rejected by admin'
        })
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error rejecting review:', error);
    throw error;
  } finally {
    setRejectLoading(prev => ({ ...prev, [reviewId]: false }));
  }
};

// Handle approve action with confirmation dialog
const handleApprove = async (reviewId) => {
  showConfirmationDialog({
    title: 'Approve Review',
    message: 'Are you sure you want to approve this review?',
    type: 'info',
    confirmText: 'Approve',
    onConfirm: async () => {
      try {
        const result = await approveReview(reviewId);
        
        if (result.success) {
          setReviews(reviews.map(review => 
            review.id === reviewId ? { 
              ...review, 
              status: 'approved',
              ratingBadge: result.data.ratingBadge || review.ratingBadge,
              isAdminApproveRating: true,
              approvedBy: result.data.approvedBy,
              approvedAt: result.data.approvedAt
            } : review
          ));
          
          if (selectedReview && selectedReview.id === reviewId) {
            setSelectedReview({
              ...selectedReview, 
              status: 'approved',
              ratingBadge: result.data.ratingBadge || selectedReview.ratingBadge,
              isAdminApproveRating: true,
              approvedBy: result.data.approvedBy,
              approvedAt: result.data.approvedAt
            });
          }
          
          fetchReviews(currentPage);
          showNotification(result.message || 'Review approved successfully!', 'success');
        } else {
          showNotification(`Failed to approve review: ${result.message}`, 'error');
        }
      } catch (error) {
        console.error('Error approving review:', error);
        showNotification(error.message || 'Error approving review. Please try again.', 'error');
      }
    },
    onCancel: () => {}
  });
};

// Handle reject action
const handleReject = (reviewId) => {
  setRejectingReviewId(reviewId);
  setRejectionReason('');
  setShowRejectionDialog(true);
};

// Confirm reject with reason
const confirmReject = async () => {
  if (!rejectionReason.trim()) {
    showNotification('Please provide a rejection reason', 'error');
    return;
  }

  try {
    const result = await rejectReview(rejectingReviewId, rejectionReason);
    
    if (result.success) {
      setReviews(reviews.map(review => 
        review.id === rejectingReviewId ? { 
          ...review, 
          status: 'rejected',
          isAdminApproveRating: false,
          rejectionReason: result.data.rejectionReason,
          rejectedBy: result.data.rejectedBy,
          rejectedAt: result.data.rejectedAt
        } : review
      ));
      
      if (selectedReview && selectedReview.id === rejectingReviewId) {
        setSelectedReview({
          ...selectedReview, 
          status: 'rejected',
          isAdminApproveRating: false,
          rejectionReason: result.data.rejectionReason,
          rejectedBy: result.data.rejectedBy,
          rejectedAt: result.data.rejectedAt
        });
      }
      
      setShowRejectionDialog(false);
      setRejectingReviewId(null);
      setRejectionReason('');
      fetchReviews(currentPage);
      showNotification(result.message || 'Review rejected successfully!', 'success');
    } else {
      showNotification(`Failed to reject review: ${result.message}`, 'error');
    }
  } catch (error) {
    console.error('Error rejecting review:', error);
    showNotification(error.message || 'Error rejecting review. Please try again.', 'error');
  }
};

// API: Add review to featured list (homepage pe dikhao)
const handleAddToFeatured = async (reviewId) => {
  setFeaturedLoading(prev => ({ ...prev, [reviewId]: true }));
  try {
    const response = await fetch(
      `${API_BASE_URL}/review-rating/approved-review/add-to-list?ratingId=${reviewId}`,
      {
        method: 'POST',
        headers: getHeaders(false)
      }
    );
    const data = await response.json();
    if (data.success) {
      setFeaturedReviews(prev => ({ ...prev, [reviewId]: true }));
      showNotification(data.message || 'Review added to featured list!', 'success');
    } else {
      showNotification(data.message || 'Failed to add to featured list.', 'error');
    }
  } catch (error) {
    console.error('Error adding to featured list:', error);
    showNotification('Error adding to featured list. Please try again.', 'error');
  } finally {
    setFeaturedLoading(prev => ({ ...prev, [reviewId]: false }));
  }
};

// API: Remove review from featured list (homepage se hatao)
const handleRemoveFromFeatured = async (reviewId) => {
  setFeaturedLoading(prev => ({ ...prev, [reviewId]: true }));
  try {
    const response = await fetch(
      `${API_BASE_URL}/review-rating/approved-review/remove-from-list/${reviewId}`,
      {
        method: 'DELETE',
        headers: getHeaders(false)
      }
    );
    const data = await response.json();
    if (data.success) {
      setFeaturedReviews(prev => ({ ...prev, [reviewId]: false }));
      showNotification(data.message || 'Review removed from featured list!', 'success');
    } else {
      showNotification(data.message || 'Failed to remove from featured list.', 'error');
    }
  } catch (error) {
    console.error('Error removing from featured list:', error);
    showNotification('Error removing from featured list. Please try again.', 'error');
  } finally {
    setFeaturedLoading(prev => ({ ...prev, [reviewId]: false }));
  }
};

// Toggle featured: agar already featured hai toh remove, warna add
const handleToggleFeatured = (review) => {
  const isFeatured = featuredReviews[review.id];
  if (isFeatured) {
    showConfirmationDialog({
      title: 'Remove from Featured',
      message: `"${review.title || review.product.name}" ko homepage se hatana chahte ho?`,
      type: 'warning',
      confirmText: 'Remove',
      onConfirm: () => handleRemoveFromFeatured(review.id),
      onCancel: () => {}
    });
  } else {
    handleAddToFeatured(review.id);
  }
};



  // Media Gallery Component
  const MediaGallery = ({ reviewId, media }) => {
    if (!media || media.length === 0) return null;
    
    const currentIndex = currentMediaIndex[reviewId] || 0;
    const currentMedia = media[currentIndex];
    const photoCount = media.filter(m => m.type === 'photo').length;
    const videoCount = media.filter(m => m.type === 'video').length;

    return (
      <div style={{
        margin: '16px 0',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        padding: '20px',
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {/* Media Header with Counts */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '12px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <h4 style={{
              margin: 0,
              color: '#1f2937',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              User Uploaded Media
            </h4>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {photoCount > 0 && (
                <span style={{
                  fontSize: '12px',
                  color: '#3b82f6',
                  background: '#dbeafe',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontWeight: '500'
                }}>
                  {photoCount} Photo{photoCount !== 1 ? 's' : ''}
                </span>
              )}
              {videoCount > 0 && (
                <span style={{
                  fontSize: '12px',
                  color: '#ef4444',
                  background: '#fee2e2',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontWeight: '500'
                }}>
                  {videoCount} Video{videoCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            {currentIndex + 1} of {media.length}
          </div>
        </div>

        {/* Main Media Display */}
        {mediaLoading[reviewId] ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '250px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        ) : (
          <>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '250px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1f2937',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              {/* Previous Button */}
              {media.length > 1 && (
                <button 
                  onClick={() => prevMedia(reviewId)}
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '24px',
                    color: '#1f2937',
                    zIndex: 2,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'white'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.9)'}
                >
                  ‹
                </button>
              )}
              
              {/* Media Content */}
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                {currentMedia.type === 'photo' && (
                  <img 
                    src={currentMedia.src} 
                    alt={`Media ${currentIndex + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }}
                  />
                )}
                
                {currentMedia.type === 'video' && (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <video
                      src={currentMedia.src}
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        borderRadius: '4px'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(239, 68, 68, 0.9)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      Video
                    </div>
                  </div>
                )}
                
                {/* Media Type Indicator */}
                {currentMedia.type === 'photo' && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(59, 130, 246, 0.9)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    Photo
                  </div>
                )}
              </div>
              
              {/* Next Button */}
              {media.length > 1 && (
                <button 
                  onClick={() => nextMedia(reviewId)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '24px',
                    color: '#1f2937',
                    zIndex: 2,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'white'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.9)'}
                >
                  ›
                </button>
              )}
            </div>

            {/* Media Thumbnails */}
            {media.length > 1 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                padding: '8px 0',
                marginTop: '8px'
              }}>
                {media.map((item, index) => (
                  <div 
                    key={item.mediaId}
                    style={{
                      position: 'relative',
                      flexShrink: 0,
                      cursor: 'pointer',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: index === currentIndex ? '2px solid #3b82f6' : '2px solid transparent',
                      transition: 'all 0.2s',
                      width: '60px',
                      height: '60px'
                    }}
                    onClick={() => setCurrentMediaIndex(prev => ({
                      ...prev,
                      [reviewId]: index
                    }))}
                    onMouseOver={(e) => {
                      if (index !== currentIndex) {
                        e.currentTarget.style.borderColor = '#9ca3af';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (index !== currentIndex) {
                        e.currentTarget.style.borderColor = 'transparent';
                      }
                    }}
                  >
                    {item.type === 'photo' ? (
                      <img 
                        src={item.src}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}>
                        <div style={{
                          color: '#ef4444',
                          fontSize: '24px'
                        }}>
                          ▶
                        </div>
                        <div style={{
                          position: 'absolute',
                          bottom: '2px',
                          right: '2px',
                          background: 'rgba(239, 68, 68, 0.9)',
                          color: 'white',
                          padding: '1px 3px',
                          borderRadius: '2px',
                          fontSize: '8px',
                          fontWeight: '600'
                        }}>
                          Video
                        </div>
                      </div>
                    )}
                    
                    {/* Active Indicator */}
                    {index === currentIndex && (
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#3b82f6',
                        borderRadius: '50%',
                        border: '2px solid white'
                      }} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File Info */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '6px',
              padding: '12px',
              border: '1px solid #e5e7eb',
              fontSize: '13px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <span style={{
                  color: '#374151',
                  fontWeight: '500',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}>
                  {currentMedia.fileName}
                </span>
                <span style={{
                  color: '#6b7280',
                  fontSize: '12px',
                  fontWeight: '500',
                  marginLeft: '8px',
                  textTransform: 'uppercase'
                }}>
                  {currentMedia.type}
                </span>
              </div>
              <div style={{
                display: 'flex',
                gap: '16px',
                fontSize: '12px',
                color: '#9ca3af'
              }}>
                <span>File {currentIndex + 1} of {media.length}</span>
                <span>Media ID: {currentMedia.mediaId}</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Notification Component
  const Notification = () => {
    if (!notification.show) return null;

    const getNotificationColors = () => {
      switch(notification.type) {
        case 'success': return { bg: '#d1fae5', text: '#065f46', icon: '✓' };
        case 'error': return { bg: '#fee2e2', text: '#991b1b', icon: '✗' };
        case 'info': return { bg: '#dbeafe', text: '#1e40af', icon: 'ℹ' };
        case 'warning': return { bg: '#fef3c7', text: '#92400e', icon: '⚠' };
        default: return { bg: '#f3f4f6', text: '#374151', icon: '!' };
      }
    };

    const colors = getNotificationColors();

    const getPositionStyle = () => {
      switch(notification.position) {
        case 'top-right':
          return { top: '20px', right: '20px' };
        case 'top-center':
          return { top: '20px', left: '50%', transform: 'translateX(-50%)' };
        case 'bottom-right':
          return { bottom: '20px', right: '20px' };
        default:
          return { top: '20px', right: '20px' };
      }
    };

    return (
      <div style={{
        position: 'fixed',
        ...getPositionStyle(),
        zIndex: 1003,
        animation: 'slideInRight 0.3s ease-out',
        minWidth: '300px'
      }}>
        <div style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '16px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderLeft: `4px solid ${colors.text}`
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: colors.text,
            color: colors.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {colors.icon}
          </div>
          <div style={{ flex: 1, fontSize: '14px' }}>
            {notification.message}
          </div>
          <button 
            onClick={() => setNotification(prev => ({ ...prev, show: false }))}
            style={{
              background: 'none',
              border: 'none',
              color: colors.text,
              fontSize: '18px',
              cursor: 'pointer',
              opacity: 0.7,
              padding: '4px'
            }}
            onMouseOver={(e) => e.target.style.opacity = '1'}
            onMouseOut={(e) => e.target.style.opacity = '0.7'}
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  // Confirmation Dialog Component
  const ConfirmationDialog = ({ 
    isOpen, 
    title, 
    message, 
    onConfirm, 
    onCancel, 
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "warning"
  }) => {
    if (!isOpen) return null;

    const getDialogColors = () => {
      switch(type) {
        case 'danger': return { bg: '#fee2e2', text: '#991b1b', button: '#dc2626' };
        case 'warning': return { bg: '#fef3c7', text: '#92400e', button: '#f59e0b' };
        case 'info': return { bg: '#dbeafe', text: '#1e40af', button: '#3b82f6' };
        case 'success': return { bg: '#d1fae5', text: '#065f46', button: '#10b981' };
        default: return { bg: '#f3f4f6', text: '#374151', button: '#6b7280' };
      }
    };

    const colors = getDialogColors();

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
        padding: '20px',
        backdropFilter: 'blur(2px)'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '400px',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          animation: 'modalSlideIn 0.3s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: colors.bg,
            borderRadius: '8px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.button,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '18px'
            }}>
              {type === 'danger' ? '⚠' : type === 'warning' ? '⚠' : type === 'info' ? 'ℹ' : '✓'}
            </div>
            <div>
              <h4 style={{ margin: 0, color: colors.text, fontWeight: '600' }}>{title}</h4>
              <p style={{ margin: '4px 0 0 0', color: colors.text, fontSize: '14px' }}>{message}</p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px'
          }}>
            <button 
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#374151',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: '80px'
              }}
              onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.target.style.background = 'white'}
              onClick={onCancel}
            >
              {cancelText}
            </button>
            <button 
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: colors.button,
                color: 'white',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: '80px'
              }}
              onMouseOver={(e) => e.target.style.opacity = '0.9'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  };

// Fetch already listed reviews from backend and set featuredReviews state
const fetchFeaturedList = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/review-rating/approved-review/get-list?page=0&size=200`,
      {
        method: 'GET',
        headers: getHeaders(false)
      }
    );
    const data = await response.json();
    if (data.success && data.data.reviews) {
      const featuredMap = {};
      data.data.reviews.forEach(review => {
        featuredMap[review.ratingId] = true;
      });
      setFeaturedReviews(featuredMap);
    }
  } catch (error) {
    console.error('Error fetching featured list:', error);
  }
};

  useEffect(() => {
    fetchReviews();
    fetchFeaturedList();
    
    return () => {
      Object.values(userImages).forEach(url => URL.revokeObjectURL(url));
      Object.values(productImages).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const filteredReviews = filterStatus === 'all' 
    ? reviews 
    : reviews.filter(review => review.status === filterStatus);

  // Column sorting (click a header) — matches the pattern used in
  // AllTickets.js / AllUsers.js / AllAdmins.js / AllOrders.js / AllInquiries.js.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', fontSize: '10px', color: 'white' }}>▲▼</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: 'white' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortedFilteredReviews = !sortField ? filteredReviews : [...filteredReviews].sort((a, b) => {
    if (sortField === 'date') {
      const aT = new Date(a.date).getTime() || 0;
      const bT = new Date(b.date).getTime() || 0;
      return sortDirection === 'asc' ? aT - bT : bT - aT;
    }
    if (sortField === 'rating') {
      return sortDirection === 'asc' ? a.rating - b.rating : b.rating - a.rating;
    }
    const fieldMap = {
      user: (x) => x.user?.name,
      product: (x) => x.product?.name,
      title: (x) => x.title,
      status: (x) => x.status,
    };
    const aV = (fieldMap[sortField]?.(a) || '').toString().toLowerCase();
    const bV = (fieldMap[sortField]?.(b) || '').toString().toLowerCase();
    if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
    if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleStatusChange = async (reviewId, newStatus) => {
    if (newStatus === 'approved') {
      await handleApprove(reviewId);
    } else if (newStatus === 'rejected') {
      handleReject(reviewId);
    }
    
    if (showModal) {
      setShowModal(false);
    }
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    setShowModal(true);
    // Fetch media if not already loaded
    if (!reviewMedia[review.id]) {
      fetchReviewMedia(review.id);
    }
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return '✓';
      case 'pending': return '⏳';
      case 'rejected': return '✗';
      default: return '';
      
    }
  };

  const getStatusStyle = (status) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    };
    
    switch(status) {
      case 'pending':
        return {...baseStyle, background: '#fff3cd', color: '#856404'};
      case 'approved':
        return {...baseStyle, background: '#d4edda', color: '#155724'};
      case 'rejected':
        return {...baseStyle, background: '#f8d7da', color: '#721c24'};
      default:
        return baseStyle;
    }
  };

  const getRatingBadgeStyle = (badge) => {
    const baseStyle = {
      padding: '3px 8px',
      borderRadius: '10px',
      fontSize: '10px',
      fontWeight: '600',
      marginLeft: '8px'
    };
    
    switch(badge) {
      case 'TOP_RATED':
        return {...baseStyle, background: '#ffd700', color: '#856404'};
      case 'MEDIUM_RATED':
        return {...baseStyle, background: '#c0c0c0', color: '#333'};
      case 'LOW_RATED':
        return {...baseStyle, background: '#cd7f32', color: '#fff'};
      default:
        return {...baseStyle, background: '#e5e7eb', color: '#4b5563'};
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#666',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box
                component="img"
                src="/image/footer-logo-small.svg"  
                alt="Loading..."
                sx={{
                  width: 120,
                  height: 'auto',
                  mb: 2,
                  opacity: 0.8
                }}
              />

            {/* </Box> */}
       </div>
    );
  }

  return (
    <div style={{
      padding: '20px'
    }}>
      {/* Notification Component */}
      <Notification />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.show}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={confirmationDialog.onCancel}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />

      {/* Main Content */}
      <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
            All Reviews
            <span style={{ display: 'block', width: 36, height: 3, background: '#e11d48', borderRadius: 2, marginTop: 6 }}></span>
          </h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '8px 32px 8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  color: '#1f2937',
                  cursor: 'pointer',
                  minWidth: '160px',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none'
                }}
              >
                <option value="all">All Reviews</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                marginTop: '-2px',
                pointerEvents: 'none',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '5px solid #666'
              }}></span>
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              backgroundColor: '#f8f9fa',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              Page {currentPage + 1} of {totalPages}
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '70vh'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{
                backgroundColor: '#d32f2f'
              }}>
                <th onClick={() => handleSort('date')} style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>Date{getSortIcon('date')}</th>
                <th onClick={() => handleSort('user')} style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>User{getSortIcon('user')}</th>
                <th onClick={() => handleSort('product')} style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>Product{getSortIcon('product')}</th>
                <th onClick={() => handleSort('rating')} style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>Rating{getSortIcon('rating')}</th>
                <th onClick={() => handleSort('title')} style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>Review Title{getSortIcon('title')}</th>
                <th onClick={() => handleSort('status')} style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap'
                }}>Status{getSortIcon('status')}</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white'
                }}>⭐ Featured</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  backgroundColor: '#d32f2f',
                  fontWeight: '600',
                  color: 'white'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredReviews.map(review => {
                const media = reviewMedia[review.id] || [];
                const hasMedia = media.length > 0;
                const photoCount = media.filter(m => m.type === 'photo').length;
                const videoCount = media.filter(m => m.type === 'video').length;
                
                return (
                  <tr key={review.id} style={{
                    borderBottom: '1px solid #eee',
                    ...(review.status === 'pending' ? { backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' } : {}),
                  }}>
                    <td style={{
                      padding: '12px 16px',
                      color: '#666',
                      whiteSpace: 'nowrap',
                      textAlign: 'center'
                    }}>
                      {review.status === 'pending' && (
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ff9800', marginRight: '6px' }}></span>
                      )}
                      {review.date}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <img 
                          src={review.user.avatar} 
                          alt={review.user.name}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <span style={{ fontWeight: '500' }}>{review.user.name}</span>
                          {review.verifiedPurchase && (
                            <span style={{
                              fontSize: '10px',
                              color: '#10b981',
                              background: '#d1fae5',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              display: 'inline-block',
                              marginTop: '2px',
                              width: 'fit-content'
                            }}>
                              ✓ Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <img 
                          src={review.product.image} 
                          alt={review.product.name}
                          style={{
                            width: '40px',
                            height: '30px',
                            borderRadius: '4px',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}>
                          <span>{review.product.name}</span>
                          <span style={{
                            fontSize: '11px',
                            color: '#666',
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            width: 'fit-content'
                          }}>
                            {review.product.category}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{
                          color: '#fbbf24',
                          fontSize: '14px',
                          letterSpacing: '1px'
                        }}>
                          {renderStars(review.rating)}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{
                            fontSize: '12px',
                            color: '#666'
                          }}>
                            ({review.rating}/5)
                          </span>
                          {review.ratingBadge && (
                            <span style={getRatingBadgeStyle(review.ratingBadge)}>
                              {review.ratingBadge.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      maxWidth: '200px',
                      minWidth: '150px'
                    }}>
                      {review.title ? (
                        <div style={{
                          fontWeight: '500',
                          marginBottom: '4px',
                          color: '#1f2937',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {review.title}
                        </div>
                      ) : (
                        <div style={{
                          color: '#9ca3af',
                          fontStyle: 'italic',
                          fontSize: '13px'
                        }}>
                          No title
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={getStatusStyle(review.status)}>
                        {getStatusIcon(review.status)} {review.status}
                      </span>
                      {hasMedia && (
                        <div style={{
                          marginTop: '4px',
                          display: 'flex',
                          gap: '4px',
                          flexWrap: 'wrap'
                        }}>
                          {photoCount > 0 && (
                            <span style={{
                              fontSize: '10px',
                              color: '#3b82f6',
                              background: '#dbeafe',
                              padding: '2px 4px',
                              borderRadius: '4px'
                            }}>
                              📸 {photoCount}
                            </span>
                          )}
                          {videoCount > 0 && (
                            <span style={{
                              fontSize: '10px',
                              color: '#ef4444',
                              background: '#fee2e2',
                              padding: '2px 4px',
                              borderRadius: '4px'
                            }}>
                              🎬 {videoCount}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Featured Toggle Column */}
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {review.status === 'approved' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                          {/* Toggle Switch */}
                          <div
                            onClick={() => !featuredLoading[review.id] && handleToggleFeatured(review)}
                            title={featuredReviews[review.id] ? 'Homepage se hatao' : 'Homepage pe dikhao'}
                            style={{
                              position: 'relative',
                              width: '44px',
                              height: '24px',
                              borderRadius: '12px',
                              background: featuredLoading[review.id]
                                ? '#d1d5db'
                                : featuredReviews[review.id]
                                ? '#10b981'
                                : '#d1d5db',
                              cursor: featuredLoading[review.id] ? 'not-allowed' : 'pointer',
                              transition: 'background 0.25s',
                              flexShrink: 0
                            }}
                          >
                            <div style={{
                              position: 'absolute',
                              top: '3px',
                              left: featuredReviews[review.id] ? '23px' : '3px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              background: 'white',
                              transition: 'left 0.25s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                          </div>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '500',
                            color: featuredReviews[review.id] ? '#065f46' : '#6b7280'
                          }}>
                            {featuredLoading[review.id] ? '...' : featuredReviews[review.id] ? 'Listed' : 'Unlisted'}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        justifyContent: 'center'
                      }}>
                        <button 
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#3b82f6',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#2563eb'}
                          onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                          onClick={() => handleViewReview(review)}
                        >
                          View
                        </button>
                        <button 
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            background: (review.status === 'approved' || review.status === 'rejected') ? '#6c757d' : '#28a745',
                            color: 'white',
                            cursor: (review.status === 'approved' || review.status === 'rejected') ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            opacity: (review.status === 'approved' || review.status === 'rejected') ? 0.5 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (review.status === 'pending') {
                              e.target.style.background = '#218838';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (review.status === 'pending') {
                              e.target.style.background = '#28a745';
                            }
                          }}
                          onClick={() => handleStatusChange(review.id, 'approved')}
                          disabled={review.status === 'approved' || review.status === 'rejected' || approveLoading[review.id]}
                          title={review.status === 'approved' ? 'Already approved' : 'Approve this review'}
                        >
                          {approveLoading[review.id] ? 'Processing...' : 'Approve'}
                        </button>
                        <button 
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            background: (review.status === 'rejected' || review.status === 'approved') ? '#6c757d' : '#dc3545',
                            color: 'white',
                            cursor: (review.status === 'rejected' || review.status === 'approved') ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            opacity: (review.status === 'rejected' || review.status === 'approved') ? 0.5 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (review.status === 'pending') {
                              e.target.style.background = '#c82333';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (review.status === 'pending') {
                              e.target.style.background = '#dc3545';
                            }
                          }}
                          onClick={() => handleStatusChange(review.id, 'rejected')}
                          disabled={review.status === 'rejected' || review.status === 'approved' || rejectLoading[review.id]}
                          title={review.status === 'rejected' ? 'Already rejected' : 'Reject this review'}
                        >
                          {rejectLoading[review.id] ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredReviews.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666',
            background: 'white',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <p>No reviews found for the selected filter.</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            marginTop: '20px',
            padding: '10px'
          }}>
            <button 
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                background: currentPage === 0 ? '#f8f9fa' : 'white',
                color: currentPage === 0 ? '#aaa' : '#333',
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                borderRadius: '4px'
              }}
              onClick={() => currentPage > 0 && fetchReviews(currentPage - 1)}
              disabled={currentPage === 0}
            >
              Previous
            </button>
            <span style={{ color: '#666' }}>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button 
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                background: currentPage === totalPages - 1 ? '#f8f9fa' : 'white',
                color: currentPage === totalPages - 1 ? '#aaa' : '#333',
                cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
                borderRadius: '4px'
              }}
              onClick={() => currentPage < totalPages - 1 && fetchReviews(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Rejection Dialog */}
      {showRejectionDialog && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}
          onClick={() => setShowRejectionDialog(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#1f2937',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              Reject Review #{rejectingReviewId}
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#374151',
                fontWeight: '500'
              }}>
                Rejection Reason *
              </label>
              <textarea
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                  transition: 'border-color 0.2s'
                }}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows="4"
              />
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button 
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#374151',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#f3f4f6';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'white';
                }}
                onClick={() => {
                  setShowRejectionDialog(false);
                  setRejectionReason('');
                  setRejectingReviewId(null);
                }}
              >
                Cancel
              </button>
              <button 
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#ef4444',
                  color: 'white',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#dc2626'}
                onMouseOut={(e) => e.target.style.background = '#ef4444'}
                onClick={confirmReject}
                disabled={!rejectionReason.trim() || rejectLoading[rejectingReviewId]}
              >
                {rejectLoading[rejectingReviewId] ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog */}
      {showModal && selectedReview && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Review Details - #{selectedReview.id}
              </h3>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = '#6b7280';
                }}
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {/* Review Summary */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  backgroundColor: '#f9fafb',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontWeight: '500', color: '#374151' }}>
                      Review #{selectedReview.id}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={getStatusStyle(selectedReview.status)}>
                        {selectedReview.status}
                      </span>
                      {selectedReview.ratingBadge && (
                        <span style={getRatingBadgeStyle(selectedReview.ratingBadge)}>
                          {selectedReview.ratingBadge.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    <span>Submitted: {selectedReview.date}</span>
                    {/* <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      👍 {selectedReview.helpfulCount} found helpful
                    </span> */}
                    {selectedReview.shippingOption && (
                      <span>Shipping: {selectedReview.shippingOption}</span>
                    )}
                  </div>
                  
                  {/* Approval/Rejection Details */}
                  {selectedReview.approvedBy && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#d4edda',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#155724'
                    }}>
                      <strong>Approved by:</strong> {selectedReview.approvedBy}
                      <br />
                      <strong>Approved at:</strong> {formatDateTime(selectedReview.approvedAt)}
                    </div>
                  )}
                  
                  {selectedReview.rejectedBy && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#f8d7da',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#721c24'
                    }}>
                      <strong>Rejected by:</strong> {selectedReview.rejectedBy}
                      <br />
                      <strong>Rejected at:</strong> {formatDateTime(selectedReview.rejectedAt)}
                      {selectedReview.rejectionReason && (
                        <>
                          <br />
                          <strong>Reason:</strong> {selectedReview.rejectionReason}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Media Gallery */}
              <MediaGallery 
                reviewId={selectedReview.id}
                media={reviewMedia[selectedReview.id] || []}
              />

              {/* User & Product Side by Side */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                marginBottom: '24px'
              }}>
                {/* User Information */}
                <div>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    color: '#374151',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>
                    User Information
                  </h4>
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <img 
                        src={selectedReview.user.avatar} 
                        alt={selectedReview.user.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <div>
                        <h5 style={{
                          margin: '0 0 4px 0',
                          color: '#1f2937',
                          fontSize: '15px'
                        }}>
                          {selectedReview.user.name}
                        </h5>
                        {selectedReview.verifiedPurchase && (
                          <span style={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            ✓ Verified Purchase
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Email:</span>
                        <span>{selectedReview.user.email}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Phone:</span>
                        <span>{selectedReview.user.phone}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Location:</span>
                        <span>{selectedReview.user.location}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Member Since:</span>
                        <span>{selectedReview.user.joinDate}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>User ID:</span>
                        <span style={{
                          fontFamily: 'monospace',
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          {selectedReview.userId}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Order ID:</span>
                        <span style={{
                          fontFamily: 'monospace',
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          {selectedReview.orderId}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Information */}
                <div>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    color: '#374151',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>
                    Product Information
                  </h4>
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <img 
                        src={selectedReview.product.image} 
                        alt={selectedReview.product.name}
                        style={{
                          width: '60px',
                          height: '45px',
                          borderRadius: '6px',
                          objectFit: 'cover'
                        }}
                      />
                      <div>
                        <h5 style={{
                          margin: '0 0 4px 0',
                          color: '#1f2937',
                          fontSize: '15px'
                        }}>
                          {selectedReview.product.name}
                        </h5>
                      </div>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Category:</span>
                        <span>{selectedReview.product.category}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Brand:</span>
                        <span>{selectedReview.product.brand}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>Model:</span>
                        <span>{selectedReview.product.model}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rating & Review */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  margin: '0 0 16px 0',
                  color: '#374151',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  Rating & Review
                </h4>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  {selectedReview.title && (
                    <h5 style={{
                      margin: '0 0 16px 0',
                      color: '#1f2937',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      {selectedReview.title}
                    </h5>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      color: '#fbbf24',
                      fontSize: '24px',
                      letterSpacing: '2px'
                    }}>
                      {'★'.repeat(selectedReview.rating)}
                      {'☆'.repeat(5 - selectedReview.rating)}
                    </div>
                    <span style={{ fontWeight: '600', color: '#374151' }}>
                      {selectedReview.rating}.0 out of 5
                    </span>
                  </div>
                  <div style={{
                    backgroundColor: '#f9fafb',
                    padding: '16px',
                    borderRadius: '6px',
                    borderLeft: '4px solid #3b82f6'
                  }}>
                    <p style={{ margin: 0, lineHeight: 1.6, color: '#4b5563' }}>
                      {selectedReview.comment}
                    </p>
                  </div>
                </div>
              </div>

              {/* Moderation Actions */}
              <div>
                <h4 style={{
                  margin: '0 0 16px 0',
                  color: '#3d0000ff',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  Moderation Actions
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <button 
                    style={{
                      padding: '10px 20px',
                      border: '2px solid transparent',
                      borderRadius: '6px',
                      fontWeight: '500',
                      cursor: selectedReview.status === 'approved' ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      backgroundColor: selectedReview.status === 'approved' ? '#10b981' : '#d1fae5',
                      color: selectedReview.status === 'approved' ? 'white' : '#065f46',
                      borderColor: '#10b981',
                      opacity: selectedReview.status === 'approved' ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (selectedReview.status !== 'approved') {
                        e.target.style.backgroundColor = '#10b981';
                        e.target.style.color = 'white';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedReview.status !== 'approved') {
                        e.target.style.backgroundColor = '#d1fae5';
                        e.target.style.color = '#065f46';
                      }
                    }}
                    onClick={() => handleStatusChange(selectedReview.id, 'approved')}
                    disabled={selectedReview.status === 'approved' || selectedReview.status === 'rejected' || approveLoading[selectedReview.id]}
                  >
                    {approveLoading[selectedReview.id] ? 'Processing...' : selectedReview.status === 'approved' ? '✓ Approved' : 'Approve Review'}
                  </button>
                  <button 
                    style={{
                      padding: '10px 20px',
                      border: '2px solid transparent',
                      borderRadius: '6px',
                      fontWeight: '500',
                      cursor: selectedReview.status === 'rejected' ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      backgroundColor: selectedReview.status === 'rejected' ? '#ef4444' : '#fee2e2',
                      color: selectedReview.status === 'rejected' ? 'white' : '#991b1b',
                      borderColor: '#ef4444',
                      opacity: selectedReview.status === 'rejected' ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (selectedReview.status !== 'rejected') {
                        e.target.style.backgroundColor = '#ef4444';
                        e.target.style.color = 'white';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedReview.status !== 'rejected') {
                        e.target.style.backgroundColor = '#fee2e2';
                        e.target.style.color = '#991b1b';
                      }
                    }}
                    onClick={() => handleStatusChange(selectedReview.id, 'rejected')}
                    disabled={selectedReview.status === 'rejected' || selectedReview.status === 'approved' || rejectLoading[selectedReview.id]}
                  >
                    {rejectLoading[selectedReview.id] ? 'Processing...' : selectedReview.status === 'rejected' ? '✗ Rejected' : 'Reject Review'}
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '0 0 12px 12px'
            }}>
              <button 
                style={{
                  padding: '10px 24px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#4b5563'}
                onMouseOut={(e) => e.target.style.background = '#6b7280'}
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS Animations */}
      <style jsx="true">{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AllReviews;